import { Dispatch, SetStateAction, useState } from 'react'
import { Table, TableBody, TableContainer } from '@mui/material'

import { THead } from './t-head.component'
import { TBodyRow } from './t-body-row.component'
import { MediaAddForm } from '../add-form'
import { IMediaListItem, MediaConnectionType } from 'src/types'
import { useItemsRemoveConfirm, useRemoveMedia, useTableItemsCheck } from 'src/hooks'

interface IProps {
  data: IMediaListItem[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const getMediaConnectionTitle = (type: MediaConnectionType): string => {
  const titles: { [key in MediaConnectionType]: string } = {
    account: 'Accounts',
    impact: 'Impacts',
    journey: 'Journeys',
    product: 'Products',
    scan: 'Scans',
    scan_draft: 'Scan drafts',
    step: 'Steps',
  }
  return titles[type]
}

export const MediaTable = ({ data, sort, setSort }: IProps) => {
  const [open, setOpen] = useState(false)
  const { mutateAsync } = useRemoveMedia()
  const { setRemoveConfirm } = useItemsRemoveConfirm()
  const {
    handleAllItemsCheck,
    handleItemCheck,
    isItemSelected,
    selectedItemsIds,
    setSelectedItemsIds,
    isIndeterminated,
    isAllChecked,
    isAllUnchecked,
  } = useTableItemsCheck({
    items: data ?? [],
  })

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    const selectedItems = data!.filter((item) => selectedItemsIds.includes(item.id))

    const groupedRelations = selectedItems.reduce((acc: any, item) => {
      const type = item.connection?.type
      const title = item.connection?.title
      if (!type || !title) {
        return acc
      }

      if (type) {
        if (!acc[type]) {
          acc[type] = new Set<string>()
        }
        acc[type].add(title)
      }

      return acc
    }, {})

    const itemsRelations = Object.keys(groupedRelations).map((type) => {
      return {
        title: getMediaConnectionTitle(type as MediaConnectionType),
        relations: Array.from(groupedRelations[type]) as string[],
      }
    })

    setRemoveConfirm({
      handleConfirm: async () => {
        await mutateAsync(selectedItemsIds)
      },
      itemsTitles: selectedItems.map((item) => item.title),
      itemsRelations: itemsRelations,

      itemsType: selectedItemsIds.length > 1 ? 'media items' : 'media item',
    })

    setSelectedItemsIds([])
  }

  return (
    <>
      <TableContainer>
        <Table>
          <THead
            handleCreateOpen={handleOpen}
            handleSortChange={handleSortChange}
            sort={sort}
            handleAllItemsCheck={handleAllItemsCheck}
            handleItemsRemove={handleItemsRemove}
            isAllChecked={isAllChecked}
            isAllUnchecked={isAllUnchecked}
            isIndeterminated={isIndeterminated}
          />
          <TableBody>
            {data?.map((row) => (
              <TBodyRow
                key={row.id}
                row={row}
                isItemSelected={isItemSelected}
                handleItemCheck={handleItemCheck}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <MediaAddForm open={open} onClose={handleClose} />
    </>
  )
}
